import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { mileageApi, MileageLog } from '../services/mileageApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { driversApi, Driver } from '../services/driversApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Mileage: React.FC = () => {
    const { t } = useLanguage();
    
    
    const [logs, setLogs] = useState<MileageLog[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingLog, setEditingLog] = useState<MileageLog | null>(null);
    const formatDateForInput = (d: any) => {
        if (!d) return '';
        const date = new Date(d);
        return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
    };

    const [formData, setFormData] = useState<Partial<MileageLog>>({
        vehicleId: undefined,
        driverId: undefined,
        startDate: new Date(),
        endDate: new Date(),
        startKm: 0,
        endKm: 0
    });

    const loadData = async () => {
        try {
            const [logsRes, vehRes, drvRes] = await Promise.all([
                mileageApi.getAll(),
                vehiclesApi.getAll(),
                driversApi.getAll()
            ]);
            
            if (logsRes.success) setLogs(logsRes.data);
            if (vehRes.success) setVehicles(vehRes.data);
            if (drvRes.success) setDrivers(drvRes.data);
        } catch (error) {
            console.error('Failed to load mileage logs:', error);
            blobticMessage.error(t('common.messages.error'), 'Failed to load mileage logs');
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenDialog = (log?: MileageLog) => {
        if (log) {
            setEditingLog(log);
            setFormData({ ...log });
        } else {
            setEditingLog(null);
            setFormData({
                vehicleId: vehicles.length > 0 ? vehicles[0].id : undefined,
                driverId: drivers.length > 0 ? drivers[0].id : undefined,
                startDate: new Date(),
                endDate: new Date(),
                startKm: 0,
                endKm: 0
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.vehicleId || formData.startKm === undefined || formData.endKm === undefined) {
            blobticMessage.error(t('common.messages.warning') || 'Warning', t('zoom.check.fillRequired'));
            return;
        }

        if (formData.endKm < formData.startKm) {
            blobticMessage.error(t('common.messages.warning') || 'Warning', t('parc.mileage.errorEndKmLessThanStartKm') || 'Le kilométrage de fin ne peut pas être inférieur à celui de début.');
            return;
        }

        try {
            let response;
            if (editingLog) {
                response = await mileageApi.update(editingLog.id, formData);
            } else {
                response = await mileageApi.create(formData);
            }

            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                setIsDialogOpen(false);
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving mileage log:', error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.error'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.confirm.delete') || 'Are you sure?')) return;
        try {
            const response = await mileageApi.delete(id);
            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.delete_error'));
        }
    };

    const columns = [
        { id: 'vehicleRegistration', label: t('parc.gpsTracking.vehicle') || 'Véhicule', minWidth: 120 },
        { id: 'driverName', label: t('parc.trips.driver') || 'Conducteur', minWidth: 150 },
        {
            id: 'startDate',
            label: t('parc.mileage.startDate') || 'Date Début',
            minWidth: 120,
            format: (value: Date) => value.toLocaleDateString()
        },
        {
            id: 'endDate',
            label: t('parc.mileage.endDate') || 'Date Fin',
            minWidth: 120,
            format: (value: Date) => value.toLocaleDateString()
        },
        { id: 'startKm', label: t('parc.mileage.startKm') || 'KM Début', minWidth: 120, format: (val: number) => `${val} km` },
        { id: 'endKm', label: t('parc.mileage.endKm') || 'KM Fin', minWidth: 120, format: (val: number) => `${val} km` },
        { id: 'distance', label: t('parc.mileage.distance') || 'Distance', minWidth: 120, format: (val: number) => `${val} km` }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.mileage.newLog')}
        </Button>
    );

    return (
        <GenericPage title={t('menu.parc.mileage') || 'Kilométrage'} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={logs}
                onEdit={handleOpenDialog}
                onDelete={(v: any) => handleDelete(v.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingLog ? t('parc.mileage.editLog') : t('parc.mileage.addLog')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('parc.gpsTracking.vehicle')}</InputLabel>
                                <Select
                                    value={formData.vehicleId || ''}
                                    label={t('parc.gpsTracking.vehicle')}
                                    onChange={(e) => setFormData({ ...formData, vehicleId: Number(e.target.value) })}
                                >
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.registrationNumber} ({v.brand} {v.model})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.trips.driver')}</InputLabel>
                                <Select
                                    value={formData.driverId || ''}
                                    label={t('parc.trips.driver')}
                                    onChange={(e) => setFormData({ ...formData, driverId: Number(e.target.value) })}
                                >
                                    {drivers.map((d) => (
                                        <MenuItem key={d.id} value={d.id}>
                                            {d.firstName} {d.lastName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label={t('parc.mileage.startDate')}
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.startDate)}
                                onChange={(e) => setFormData({ ...formData, startDate: new Date(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label={t('parc.mileage.endDate')}
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.endDate)}
                                onChange={(e) => setFormData({ ...formData, endDate: new Date(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.mileage.startKm')}
                                value={formData.startKm || ''}
                                onChange={(e) => setFormData({ ...formData, startKm: Number(e.target.value) })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.mileage.endKm')}
                                value={formData.endKm || ''}
                                onChange={(e) => setFormData({ ...formData, endKm: Number(e.target.value) })}
                                required
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Mileage;
